import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/core/utils';
import { Icon } from './Icon';

/** Gap kept between the card and the viewport edges, px. */
const EDGE = 8;
/** Size of the tip triangle (as MapLibre's popup tip), px. */
const TIP = 10;
/** Time to move the mouse from the button onto the card before it closes, ms. */
const LEAVE_DELAY = 150;
/** Fade in/out time of the card, ms (matches its `duration-*` class). */
const FADE = 150;

interface Props {
  /** Accessible name of the button, e.g. "About weather stations". */
  label: string;
  children: ReactNode;
  className?: string;
}

/** How it was opened: hovering closes when the mouse leaves; a click or tap keeps it open until dismissed. */
type OpenState = 'closed' | 'hover' | 'pinned';

/**
 * An "i" button showing help in a card styled like the map popups. Mouse users hover or click it,
 * touch users tap it; a tap elsewhere or Escape closes it. The card is portalled to <body> so panels
 * that scroll or move (the bottom sheet) don't clip it.
 */
export function InfoTip({ label, children, className }: Props) {
  const id = useId();
  const [state, setState] = useState<OpenState>('closed');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const leaveTimer = useRef<number>(undefined);
  const open = state !== 'closed';
  // The card stays in the DOM while it fades out after closing.
  const [rendered, setRendered] = useState(false);
  if (open && !rendered) setRendered(true);

  useEffect(() => {
    if (open || !rendered) return;
    const timer = window.setTimeout(() => setRendered(false), FADE);
    return () => clearTimeout(timer);
  }, [open, rendered]);

  // Above the button if it fits, below otherwise; kept inside the viewport and following the button.
  // Written to the DOM directly: it follows the button's layout, not React state.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const card = cardRef.current!;
      const button = buttonRef.current!.getBoundingClientRect();
      const { width, height } = card.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const centre = button.left + button.width / 2;
      const left = Math.min(Math.max(EDGE, centre - width / 2), vw - EDGE - width);
      const below = button.top - TIP - height < EDGE;
      card.style.left = `${left}px`;
      card.style.top = `${below ? button.bottom + TIP : button.top - TIP - height}px`;
      card.style.visibility = 'visible';
      card.dataset.side = below ? 'below' : 'above';
      tipRef.current!.style.left = `${Math.min(Math.max(TIP + 6, centre - left), width - TIP - 6)}px`;
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // A press anywhere else (map, panel, sheet handle) or Escape closes it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: globalThis.PointerEvent) => {
      const target = e.target as Node;
      if (!buttonRef.current?.contains(target) && !cardRef.current?.contains(target)) setState('closed');
    };
    const onKey = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && setState('closed');
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  function onPointerEnter(e: PointerEvent) {
    if (e.pointerType !== 'mouse') return;
    clearTimeout(leaveTimer.current);
    setState((s) => (s === 'closed' ? 'hover' : s));
  }

  function onPointerLeave(e: PointerEvent) {
    if (e.pointerType !== 'mouse') return;
    clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(
      () => setState((s) => (s === 'hover' ? 'closed' : s)),
      LEAVE_DELAY,
    );
  }

  function onKeyDown(e: KeyboardEvent) {
    // Escape closes only the card, not whatever contains the button (e.g. the bottom sheet).
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setState('closed');
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        // The tap target is wider than the icon: the negative margin keeps the icon close to the text before it.
        className={cx(
          '-my-1 -ml-2 inline-flex size-10 cursor-pointer items-center justify-center rounded-full text-hint hover:text-accent md:-ml-1 md:size-8',
          open && 'text-accent',
          className,
        )}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        // A click on the open card made by hovering pins it rather than closing it.
        onClick={() => setState((s) => (s === 'pinned' ? 'closed' : 'pinned'))}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onKeyDown={onKeyDown}
      >
        <Icon className="size-[18px]" strokeWidth={1.75}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v5" />
          <path d="M12 7.5v.01" strokeWidth={2.5} />
        </Icon>
      </button>
      {rendered &&
        createPortal(
          <div
            ref={cardRef}
            id={id}
            role="tooltip"
            className={cx(
              'group invisible fixed top-0 left-0 z-50 w-max max-w-[min(290px,calc(100vw-16px))] rounded-popup bg-white px-3 pt-2.5 pb-3 text-sm filter-(--popup-shadow)',
              'motion-safe:transition-opacity motion-safe:duration-150 motion-safe:starting:opacity-0',
              !open && 'pointer-events-none opacity-0',
            )}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
          >
            {children}
            {/* MapLibre's popup tip: a 10px triangle pointing at the button. */}
            <span
              ref={tipRef}
              aria-hidden
              className="absolute top-full size-0 -translate-x-1/2 border-x-10 border-t-10 border-x-transparent border-t-white group-data-[side=below]:top-auto group-data-[side=below]:bottom-full group-data-[side=below]:border-t-0 group-data-[side=below]:border-b-10 group-data-[side=below]:border-b-white"
            />
          </div>,
          document.body,
        )}
    </>
  );
}
